import axios from 'axios';
import { API_URL } from './api';

const api = axios.create({
    baseURL: `${API_URL}/api/v1`,
    headers: {
        'Content-Type': 'application/json',
    },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export const uploadBrandDocuments = async (files: File[]) => {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    
    const response = await api.post('/brand-voice/upload-documents', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

export const generateSystemPrompt = async (manualInstructions?: string, manualRules?: string, platform?: string | null) => {
    // Increased timeout for large PDF processing with Vision AI
    const response = await api.post('/brand-voice/generate-prompt', {
        manual_instructions: manualInstructions,
        manual_rules: manualRules,
        platform: platform  // Add platform parameter for platform-specific customization
    }, {
        timeout: 300000  // 5 minutes timeout for large documents
    });
    return response.data;
};

export const savePrompt = async (promptName: string, systemPrompt: string, brandSummary?: string, platforms?: string[]) => {
    const formData = new FormData();
    formData.append('prompt_name', promptName);
    formData.append('system_prompt', systemPrompt);
    if (brandSummary) {
        formData.append('brand_summary', brandSummary);
    }
    if (platforms) {
        formData.append('platforms', JSON.stringify(platforms));
    }
    
    const response = await api.post('/brand-voice/save-prompt', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

export const getActivePrompt = async () => {
    const response = await api.get('/brand-voice/active-prompt');
    return response.data;
};

export const getUploadedDocuments = async () => {
    const response = await api.get('/brand-voice/documents');
    return response.data;
};

export const deleteDocument = async (documentId: number) => {
    const response = await api.delete(`/brand-voice/documents/${documentId}`);
    return response.data;
};

export const getPromptHistory = async () => {
    const response = await api.get('/brand-voice/prompts/history');
    return response.data;
};

export const getPromptById = async (promptId: number) => {
    const response = await api.get(`/brand-voice/prompts/${promptId}`);
    return response.data;
};

export const updatePrompt = async (promptId: number, data: {
    prompt_name?: string;
    system_prompt?: string;
    brand_summary?: string;
    key_terms?: string;
    tone_attributes?: string;
    platforms?: string[];
}) => {
    const formData = new FormData();
    if (data.prompt_name) formData.append('prompt_name', data.prompt_name);
    if (data.system_prompt) formData.append('system_prompt', data.system_prompt);
    if (data.brand_summary) formData.append('brand_summary', data.brand_summary);
    if (data.key_terms) formData.append('key_terms', data.key_terms);
    if (data.tone_attributes) formData.append('tone_attributes', data.tone_attributes);
    if (data.platforms) formData.append('platforms', JSON.stringify(data.platforms));
    
    const response = await api.put(`/brand-voice/prompts/${promptId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
};

export const activatePrompt = async (promptId: number, deactivatePlatforms?: string) => {
    const url = `/brand-voice/prompts/${promptId}/activate${deactivatePlatforms ? `?deactivate_platforms=${deactivatePlatforms}` : ''}`;
    const response = await api.post(url);
    return response.data;
};

export const deletePrompt = async (promptId: number) => {
    const response = await api.delete(`/brand-voice/prompts/${promptId}`);
    return response.data;
};
